<script>
  import { _, locale as appLocale } from '@sveltia/i18n';
  import {
    Button,
    FloatingActionButtonWrapper,
    Infobar,
    Spacer,
    Toolbar,
    TruncatedText,
  } from '@sveltia/ui';
  import { sanitize } from 'isomorphic-dompurify';
  import { marked } from 'marked';

  import BackButton from '$lib/components/common/page-toolbar/back-button.svelte';
  import ReorderControls from '$lib/components/contents/list/reorder-controls.svelte';
  import DeleteEntriesDialog from '$lib/components/contents/shared/delete-entries-dialog.svelte';
  import CreateEntryButton from '$lib/components/contents/toolbar/create-entry-button.svelte';
  import { goBack } from '$lib/services/app/navigation';
  import { getCollectionLabel, selectedCollection } from '$lib/services/contents/collection';
  import { selectedEntries } from '$lib/services/contents/collection/entries';
  import {
    collectionState,
    listedEntries,
    reordering,
    setReorderMode,
  } from '$lib/services/contents/collection/view';
  import { env } from '$lib/services/user/env.svelte';
  import { openAuthoring } from '$lib/services/workflow/open-authoring';

  /**
   * Options for {@link sanitize}.
   */
  const SANITIZE_OPTIONS = {
    ALLOWED_TAGS: ['strong', 'em', 'del', 'code', 'a'],
    ALLOWED_ATTR: ['href'],
  };

  let showDeleteDialog = $state(false);

  /**
   * Parse the given string as Markdown and sanitize the result to only allow certain tags.
   * @param {string} str Original string.
   * @returns {string} Sanitized string.
   */
  const _sanitize = (str) =>
    sanitize(/** @type {string} */ (marked.parseInline(str)), SANITIZE_OPTIONS);

  /* v8 ignore start -- only read while a collection is selected, once the app locale is loaded */
  const name = $derived(selectedCollection.current?.name ?? '');
  const collectionLabel = $derived(
    // `appLocale.current` is a key, because `getCollectionLabel` can return a localized label
    appLocale.current && selectedCollection.current
      ? getCollectionLabel(selectedCollection.current)
      : name,
  );
  /* v8 ignore stop */
  const description = $derived(selectedCollection.current?.description);
  const {
    isEntryCollection,
    canCreate,
    canDelete,
    canReorder,
    quota,
    remaining,
    nearingQuota,
    creationDisabled,
  } = $derived(collectionState.current);
  const deleteDisabled = $derived(!selectedEntries.current.length || !canDelete);
</script>

{#if selectedCollection.current}
  <Toolbar variant="primary" ariaLabel={_('collection')}>
    {#if env.isSmallScreen}
      <BackButton
        aria-label={_('back_to_collection_list')}
        onclick={() => {
          goBack('/collections');
        }}
      />
    {/if}
    <h2 role="none"><bdi>{collectionLabel}</bdi></h2>
    {#if env.isSmallScreen}
      <Spacer flex />
    {:else}
      <div role="none" class="description">
        <TruncatedText>
          <bdi>{@html _sanitize(description || '')}</bdi>
        </TruncatedText>
      </div>
    {/if}
    {#if isEntryCollection && reordering.current}
      <ReorderControls />
    {:else if isEntryCollection}
      <!-- Taking a published entry off the site is a maintainer’s call, and the entry list has no
      other bulk action, so a contributor has nothing to select entries for -->
      {#if !env.isSmallScreen && !openAuthoring.current}
        <Button
          variant="ghost"
          label={_('delete')}
          aria-label={_('delete_selected_entries', {
            values: { count: selectedEntries.current.length },
          })}
          disabled={deleteDisabled}
          onclick={() => {
            showDeleteDialog = true;
          }}
        />
      {/if}
      {#if canReorder}
        <Button
          variant="ghost"
          label={_('reorder')}
          aria-label={_('reorder_entries')}
          disabled={!listedEntries.current.length}
          onclick={() => {
            setReorderMode(true);
          }}
        />
      {/if}
      <FloatingActionButtonWrapper>
        {#if !env.isSmallScreen || (listedEntries.current.length && !creationDisabled)}
          <CreateEntryButton
            collectionName={name}
            label={env.isSmallScreen ? undefined : _('create')}
            keyShortcuts="Accel+E"
          />
        {/if}
      </FloatingActionButtonWrapper>
    {/if}
  </Toolbar>
  {#if isEntryCollection && (creationDisabled || nearingQuota)}
    <Infobar
      dismissible={false}
      --sui-infobar-border-width="1px 0"
      --sui-infobar-message-justify-content="center"
    >
      {#if !canCreate}
        {_('creating_entries_disabled_by_admin')}
      {:else if creationDisabled}
        {_('creating_entries_disabled_by_quota', { values: { quota } })}
      {:else}
        {_('creating_entries_nearing_quota', { values: { quota, remaining } })}
      {/if}
    </Infobar>
  {/if}
{/if}

<DeleteEntriesDialog bind:open={showDeleteDialog} />

<style>
  h2 {
    flex: none !important;
  }

  .description {
    flex: auto;
    font-size: var(--sui-font-size-small);
    opacity: 0.8;
  }
</style>
